import { Center, type CenterProps, Spinner } from '@chakra-ui/react';
import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { loadingImage, loadingImageDark } from '@revelio/assets';

import { useFirstLoadGateBlocker } from './first-load-gate.store';
import styles from './loading.module.css';

const SPINNER_SIZES: Record<string, string> = {
  sm: 'md',
  md: 'lg',
  lg: 'xl',
};

const GIF_WIDTHS: Record<string, number> = {
  xs: 32,
  sm: 48,
  md: 56,
  lg: 64,
  xl: 80,
};

const LoadingGifContext = createContext<string | undefined>(undefined);

export const LoadingGifProvider = ({
  gif,
  children,
}: {
  gif: string | undefined;
  children: ReactNode;
}) => (
  <LoadingGifContext.Provider value={gif}>
    {children}
  </LoadingGifContext.Provider>
);

export const useLoadingGif = () => useContext(LoadingGifContext);

export interface LoadingProps {
  size?: string;
  /**
   * Whether being on screen holds the first-load overlay open. Every loader
   * blocks by default — that is what makes the overlay cover all routes
   * without each one opting in. Pass `false` from the overlay's own loader
   * (which would otherwise wait on itself forever) and from loaders that are
   * mounted but not visible.
   */
  blocksFirstLoad?: boolean;
}

export const Loading = ({
  size = 'md',
  blocksFirstLoad = true,
}: LoadingProps = {}) => {
  const loadingGif = useLoadingGif();

  useFirstLoadGateBlocker(blocksFirstLoad);

  if (!loadingGif) {
    return (
      <Center data-testid="loading" py={8}>
        <Spinner
          thickness="4px"
          speed="0.7s"
          color="green.500"
          emptyColor="gray.200"
          size={SPINNER_SIZES[size] ?? 'lg'}
        />
      </Center>
    );
  }

  const darkGif = loadingGif === loadingImage ? loadingImageDark : undefined;
  const dimensionStyle = {
    margin: 'auto',
    width: `${GIF_WIDTHS[size] ?? 56}px`,
    height: 'auto',
  } as const;

  return (
    <>
      <img
        data-testid="loading"
        className={`${styles.gif}${darkGif ? ` ${styles.withDark}` : ''}`}
        style={dimensionStyle}
        src={loadingGif}
        alt="loading"
      />
      {darkGif && (
        <img
          aria-hidden
          className={styles.gifDark}
          style={dimensionStyle}
          src={darkGif}
          alt=""
        />
      )}
    </>
  );
};

interface ICenterLoading extends CenterProps {
  className?: string;
  size?: string;
  shouldShow?: boolean;
  testId?: string;
}

export const CenterLoading = ({
  className,
  size,
  shouldShow = true,
  testId = 'data-loading',
  ...chakraProps
}: ICenterLoading) => {
  const display = useMemo(() => (shouldShow ? 'flex' : 'none'), [shouldShow]);

  return (
    <Center
      data-testid={testId}
      className={className}
      height="100%"
      width="100%"
      display={display}
      {...chakraProps}
    >
      {/* `shouldShow` only sets `display: none`, so the loader stays mounted —
          don't let a hidden one hold the first-load overlay open. */}
      <Loading size={size} blocksFirstLoad={shouldShow} />
    </Center>
  );
};

export const CenterSpinner = () => (
  <Center height="100%" width="100%">
    <Spinner color="navyBlue.500" />
  </Center>
);
